import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { SHARED_MASTERS_REGISTRY } from './sharedMastersRegistry';
import { appToast } from '../../../components/AppToast';

export default function SharedMasterEntryScreen() {
  const { module } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const searchId = (module || '').toLowerCase().trim();
  const config = SHARED_MASTERS_REGISTRY[searchId];

  // Parse ID query parameter for edit mode
  const idQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('id');
  }, [location.search]);

  const [name, setName] = useState('');
  const [status, setStatus] = useState('active'); // 'active' or 'inactive'
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Load existing entry details if in edit mode
  useEffect(() => {
    if (!config || !idQuery) {
      setName('');
      setStatus('active');
      return;
    }

    setIsLoading(true);
    // Locate the record in mock datasets
    const activeItems = config.data?.Active || [];
    const archiveItems = config.data?.Archive || [];
    const foundItem = [...activeItems, ...archiveItems].find(
      (item) => String(item.id) === String(idQuery)
    );

    const timer = setTimeout(() => {
      if (foundItem) {
        setName(foundItem.name || '');
        setStatus(foundItem.isArchived ? 'inactive' : 'active');
      } else {
        appToast.error('Record not found.');
      }
      setIsLoading(false);
    }, 150);

    return () => clearTimeout(timer);
  }, [config, idQuery]);

  if (!config) {
    return (
      <div className="mx-auto flex w-full max-w-[1460px] flex-col items-center justify-center py-20 px-4">
        <div className="max-w-md w-full bg-surface-container-lowest border border-outline-variant/20 rounded-[2rem] p-8 shadow-lg text-center flex flex-col items-center">
          <div className="h-16 w-16 bg-error-container text-error rounded-full flex items-center justify-center mb-6 select-none">
            <span className="material-symbols-outlined text-3xl">warning</span>
          </div>
          <h2 className="text-2xl font-black text-on-surface mb-2">Master Not Found</h2>
          <p className="text-on-surface-variant text-sm mb-6 leading-relaxed">
            The entry form for module <code className="px-2 py-0.5 bg-surface-container-high text-error rounded font-mono">"{module}"</code> is invalid or not configured.
          </p>
          <Link 
            to="/dashboard/personal" 
            className="w-full bg-primary hover:opacity-90 text-on-primary font-bold py-3.5 px-4 rounded-xl transition-all shadow-md focus:outline-none flex justify-center items-center"
          >
            Return to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const category = config.category || "Masters Framework";
  const labelText = `${config.title.endsWith('Master') ? config.title.slice(0, -7) : config.title} Name`;

  const handleSave = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      appToast.error(`${labelText} is required.`);
      return;
    }

    setIsSaving(true);
    setTimeout(() => {
      appToast.success(
        idQuery ? 'Configuration updated successfully.' : 'Configuration created successfully.'
      );
      setIsSaving(false);
      // Navigate back to the list screen
      navigate(`/masters/${config.id}/list`);
    }, 1000);
  };

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-7 pb-8 px-4 sm:px-6">
      
      {/* Title & Action Buttons Header */}
      <section className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between border-b border-outline-variant/10 pb-5">
        <div className="max-w-4xl">
          <p className="inline-flex rounded-full bg-secondary-container px-3 py-1 text-xs font-black uppercase tracking-[0.16em] text-on-secondary-container">
            {category}
          </p>
          <h1 className="mt-4 text-4xl font-extrabold leading-[0.98] tracking-[-0.02em] text-on-surface">
            {idQuery ? `Edit ${config.title}` : `Create ${config.title}`}
          </h1>
          <p className="mt-2 text-sm text-on-surface-variant max-w-xl leading-relaxed">
            Configure reference data for the enterprise structure. Establish standard classifications for workflows.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Link
            to={`/masters/${config.id}/list`}
            className="inline-flex items-center justify-center rounded-xl bg-surface-container-high px-8 py-3.5 text-sm font-bold text-on-surface-variant border border-outline-variant/10 transition-colors hover:bg-surface-container-highest"
          >
            Discard Changes
          </Link>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || isLoading}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-9 py-3.5 text-sm font-bold text-on-primary shadow-lg shadow-primary/20 transition-all hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="material-symbols-outlined text-base">save</span>
            {isSaving ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </section>

      {/* Loading form state indicator */}
      {isLoading ? (
        <div className="rounded-[2rem] border border-outline-variant/20 bg-surface-container-lowest px-6 py-20 text-center text-sm text-on-surface-variant font-semibold">
          Loading entry configuration details...
        </div>
      ) : (
        <section className="grid grid-cols-1 gap-8 xl:grid-cols-[1fr_360px]">
          <article className="rounded-[2rem] border border-outline-variant/20 bg-surface-container-lowest p-8 xl:p-10 shadow-sm flex flex-col gap-8">
            
            {/* Input Name field */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">
                  {labelText} <span className="text-error">*</span>
                </label>
                <span className="text-[10px] italic text-outline">Required Field</span>
              </div>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={`e.g., Enter ${labelText.toLowerCase()}`}
                className="w-full rounded-xl border-0 bg-surface-container-low px-4 py-3.5 text-base text-on-surface outline-none placeholder:text-outline-variant/70 transition-all focus:ring-2 focus:ring-primary/20"
              />
            </div>

            {/* Status Segmented Switcher */}
            <div className="grid gap-2">
              <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">
                Configuration Status
              </span>
              <div className="flex rounded-xl bg-surface-container-high p-1 gap-1 max-w-[240px]">
                <button
                  type="button"
                  onClick={() => setStatus('active')}
                  className={`flex-1 rounded-lg py-2.5 text-xs font-bold transition-all ${
                    status === 'active'
                      ? 'bg-primary text-on-primary shadow-sm'
                      : 'text-on-surface-variant hover:text-on-surface'
                  }`}
                >
                  Active
                </button>
                <button
                  type="button"
                  onClick={() => setStatus('inactive')}
                  className={`flex-1 rounded-lg py-2.5 text-xs font-bold transition-all ${
                    status === 'inactive'
                      ? 'bg-primary text-on-primary shadow-sm'
                      : 'text-on-surface-variant hover:text-on-surface'
                  }`}
                >
                  Inactive
                </button>
              </div>
            </div>

          </article>

          {/* Right Info Sidebar Panel */}
          <aside className="space-y-6">
            <article className="rounded-[2rem] border border-outline-variant/15 bg-surface-container-low p-6 flex flex-col gap-4">
              <div className="flex items-center gap-3 text-primary">
                <span className="material-symbols-outlined text-2xl">info</span>
                <h4 className="font-bold text-sm text-on-surface">Configuration Guidelines</h4>
              </div>
              <p className="text-xs leading-relaxed text-on-surface-variant font-medium">
                Ensure data entries are spelled correctly. Changes saved here will immediately propagate across dependent dropdown fields, transactions, and filters throughout the education and financials modules.
              </p>
            </article>
          </aside>
        </section>
      )}

    </div>
  );
}
